import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Segmented, Spin, Tag, Select } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import authService from '../utils/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

const fmtUsd = (n) => {
  const v = Number(n) || 0;
  return '$' + v.toFixed(v >= 1 ? 2 : 4);
};
const fmtNum = (n) => Number(n || 0).toLocaleString();

const authHeader = () => ({ Authorization: `Bearer ${authService.getToken()}` });

export default function AiUsagePage() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [listData, setListData] = useState({ total: 0, items: [] });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState({ purpose: undefined, status: undefined, provider: undefined });
  const [loading, setLoading] = useState(true);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/admin/ai-usage/summary`, {
        headers: authHeader(),
        params: { days },
      });
      setSummary(data?.data || null);
    } finally {
      setLoading(false);
    }
  };
  const loadList = async () => {
    const params = {
      limit: pageSize, offset: (page - 1) * pageSize, days,
      ...(filters.purpose && { purpose: filters.purpose }),
      ...(filters.status && { status: filters.status }),
      ...(filters.provider && { provider: filters.provider }),
    };
    const { data } = await axios.get(`${API_BASE}/admin/ai-usage/list`, {
      headers: authHeader(),
      params,
    });
    setListData(data?.data || { total: 0, items: [] });
  };
  useEffect(() => { loadSummary(); /* eslint-disable-next-line */ }, [days]);
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [days, page, pageSize, filters]);

  const t = summary?.totals || {};

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>AI Token 用量</h2>
        <Segmented
          value={days}
          onChange={setDays}
          options={[
            { label: '7 天', value: 7 },
            { label: '30 天', value: 30 },
            { label: '90 天', value: 90 },
            { label: '1 年', value: 365 },
          ]}
        />
      </div>

      <Spin spinning={loading}>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          {[
            { label: '今日', key: 'today' },
            { label: '昨日', key: 'yesterday' },
            { label: '本月', key: 'this_month' },
            { label: `近 ${days} 天`, key: 'last_n_days' },
            { label: '总计', key: 'all_time' },
          ].map(({ label, key }) => {
            const v = t[key] || { requests: 0, tokens: 0, cost_usd: 0 };
            return (
              <Col key={key} flex="1">
                <Card>
                  <Statistic title={label} value={fmtUsd(v.cost_usd)} prefix={<ThunderboltOutlined />} />
                  <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                    {fmtNum(v.requests)} 次 · {fmtNum(v.tokens)} tok
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Card title="按 Purpose" size="small">
              <Table size="small" pagination={false} rowKey="purpose"
                dataSource={summary?.by_purpose || []}
                columns={[
                  { title: 'Purpose', dataIndex: 'purpose' },
                  { title: '次数', dataIndex: 'requests', align: 'right', render: fmtNum },
                  { title: '成本', dataIndex: 'cost_usd', align: 'right', render: fmtUsd },
                ]} />
            </Card>
          </Col>
          <Col span={8}>
            <Card title="按 Model" size="small">
              <Table size="small" pagination={false} rowKey="model"
                dataSource={summary?.by_model || []}
                columns={[
                  { title: 'Model', dataIndex: 'model' },
                  { title: '次数', dataIndex: 'requests', align: 'right', render: fmtNum },
                  { title: '成本', dataIndex: 'cost_usd', align: 'right', render: fmtUsd },
                ]} />
            </Card>
          </Col>
          <Col span={8}>
            <Card title="按用户 (Top 10)" size="small">
              <Table size="small" pagination={false} rowKey={r => r.user_id || 'anon'}
                dataSource={summary?.by_user || []}
                columns={[
                  { title: '用户', dataIndex: 'username' },
                  { title: '次数', dataIndex: 'requests', align: 'right', render: fmtNum },
                  { title: '成本', dataIndex: 'cost_usd', align: 'right', render: fmtUsd },
                ]} />
            </Card>
          </Col>
        </Row>

        <Card
          title="详细记录"
          extra={
            <div style={{ display: 'flex', gap: 8 }}>
              <Select placeholder="Provider" allowClear style={{ width: 120 }}
                value={filters.provider}
                options={[{ value: 'openai', label: 'OpenAI' }, { value: 'anthropic', label: 'Anthropic' }]}
                onChange={(v) => { setFilters(f => ({ ...f, provider: v })); setPage(1); }} />
              <Select placeholder="Purpose" allowClear style={{ width: 160 }}
                value={filters.purpose}
                options={(summary?.by_purpose || []).map(p => ({ value: p.purpose, label: p.purpose }))}
                onChange={(v) => { setFilters(f => ({ ...f, purpose: v })); setPage(1); }} />
              <Select placeholder="状态" allowClear style={{ width: 100 }}
                value={filters.status}
                options={[{ value: 'ok', label: '成功' }, { value: 'error', label: '失败' }]}
                onChange={(v) => { setFilters(f => ({ ...f, status: v })); setPage(1); }} />
            </div>
          }
        >
          <Table
            size="small"
            rowKey="id"
            dataSource={listData.items}
            pagination={{
              current: page, pageSize, total: listData.total,
              onChange: (p, ps) => { setPage(p); setPageSize(ps); },
              showSizeChanger: true, pageSizeOptions: [20, 50, 100, 200],
            }}
            columns={[
              { title: '时间', dataIndex: 'createdAt', width: 140, render: t => dayjs(t).format('MM-DD HH:mm') },
              { title: '用户', dataIndex: 'username', width: 100, render: (u, r) => u || (r.userId ? `user#${r.userId}` : '—') },
              { title: 'Provider', dataIndex: 'provider', width: 90, render: (p) => <Tag color={p === 'openai' ? 'green' : 'purple'}>{p}</Tag> },
              { title: 'Model', dataIndex: 'model', width: 200 },
              { title: 'Purpose', dataIndex: 'purpose', width: 160 },
              { title: 'Source', dataIndex: 'source', ellipsis: true },
              { title: 'Tokens', dataIndex: 'totalTokens', align: 'right', width: 100, render: fmtNum },
              { title: '成本', dataIndex: 'cost_usd', align: 'right', width: 80, render: fmtUsd },
              { title: '耗时', dataIndex: 'durationMs', width: 80, render: v => v ? `${v}ms` : '—' },
              {
                title: '状态', dataIndex: 'status', width: 70,
                render: s => <Tag color={s === 'ok' ? 'success' : 'error'}>{s}</Tag>,
              },
            ]}
          />
        </Card>
      </Spin>
    </div>
  );
}
